import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import { BOOKING_DETAIL_PATH, BOOKING_LOGIN_PATH } from 'routes/paths';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import logoSvg from 'assets/logo.svg';
import 'pages/booking/BookingLogin.css';
import 'pages/booking/booking-auth-form.css';
import 'pages/booking/booking.css';

/** Register a booking guest for the space identified by the public slug. */
export default function BookingRegistration() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logoUrl, request, setPublicBranding } = useApi();
  const { _t, lang } = useLang();
  const [spaceName, setSpaceName] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    privacyAccepted: false,
    termsAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    request<any>({ method: 'GET', url: `/reservations/public/spaces/${encodeURIComponent(slug || '')}` })
      .then((data) => {
        if (!data.space?.guestRegistrationEnabled) throw new Error('Registration is not available');
        setSpaceName(data.space.name);
        setPublicBranding({
          buildingId: data.building.id,
          logoUrl: data.building.logoUrl ?? null,
          buildingName: data.building.title ?? null,
        });
      })
      .catch(() => setError(_t('Registration is not available for this space.')));
  }, [slug, request, setPublicBranding, _t]);

  /** Submit the guest registration and await email activation. */
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await request({
        method: 'POST',
        url: `/reservations/public/spaces/${encodeURIComponent(slug || '')}/registrations`,
        data: { ...form, language: lang },
      });
      setSent(true);
    } catch (requestError: any) {
      setError(requestError.response?.data?.msg || _t('Registration could not be completed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="booking-root login-page booking-registration-page" data-testid="booking-registration-page">
      <div className="booking-registration-shell">
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(BOOKING_DETAIL_PATH.insert({ slug: slug || '' }))}
        >
          <img src={IconArrowLeft} width={20} height={20} alt="" />
          {_t('Home')}
        </button>
        <section className="login-card booking-auth-card booking-registration-card">
          <div className="login-logo">
            <img src={logoUrl ?? logoSvg} alt={_t('Logo')} />
          </div>
          <h1 className="login-title">{_t('Register for bookings')}</h1>
          {spaceName && <p>{spaceName}</p>}
          {sent ? (
            <div role="status">
              <p>{_t('Check your email and use the activation link within 24 hours.')}</p>
            </div>
          ) : (
            <>
              <form onSubmit={submit}>
                <label>
                  {_t('First name')}
                  <input
                    required
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  />
                </label>
                <label>
                  {_t('Last name')}
                  <input
                    required
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  />
                </label>
                <label>
                  {_t('Email')}
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </label>
                <label>
                  {_t('Phone number')}
                  <input
                    required
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  />
                </label>
                <label>
                  {_t('Password')}
                  <input
                    required
                    minLength={8}
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                </label>
                <label className="booking-auth-consent">
                  <input
                    required
                    type="checkbox"
                    checked={form.privacyAccepted}
                    onChange={(e) => setForm({ ...form, privacyAccepted: e.target.checked })}
                  />
                  <span>
                    {_t('I agree to the processing of personal data according to the')}{' '}
                    <a href="https://admin.phillip.city/privacy-policy/" target="_blank" rel="noreferrer">
                      {_t('Privacy Policy')}
                    </a>
                    .
                  </span>
                </label>
                <label className="booking-auth-consent">
                  <input
                    required
                    type="checkbox"
                    checked={form.termsAccepted}
                    onChange={(e) => setForm({ ...form, termsAccepted: e.target.checked })}
                  />
                  <span>
                    {_t('I agree with the')}{' '}
                    <a href="https://admin.phillip.city/privacy-policy/" target="_blank" rel="noreferrer">
                      {_t('General Terms and Conditions')}
                    </a>
                  </span>
                </label>
                {error && (
                  <p className="booking-auth-error" role="alert">
                    {error}
                  </p>
                )}
                <button disabled={loading || !spaceName || !form.privacyAccepted || !form.termsAccepted}>
                  {loading ? _t('Submitting...') : _t('Register')}
                </button>
              </form>
              <div className="booking-auth-footer">
                <p className="booking-auth-copy">{_t('Already have an account?')}</p>
                <Link to={`${BOOKING_LOGIN_PATH.original}?slug=${encodeURIComponent(slug || '')}`}>{_t('Log in')}</Link>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
